import {
  NAV_PLACEMENT,
  NAV_ROUTE_ID,
  NAV_SECTION,
} from './types/nav-enums';
import { REPORTS_MORE_ITEM_NAME } from './constants/navigation-constants';
import { filterVisibleNavRoutes } from './nav-filter';
import { NAV_ROUTE_MANIFEST } from './nav-route-manifest';
import type {
  LegacyNavItem,
  NavVisibilityContext,
  PresenterNavItem,
} from './types/nav-types';
import {
  DesktopDashboardZones,
} from './types/nav-presenters-types';

function buildSectionTree(
  section: NAV_SECTION,
  placement: NAV_PLACEMENT,
  context: NavVisibilityContext,
  options: { excludeMobileBoardsDedicatedTab?: boolean } = {}
): PresenterNavItem[] {
  const visible = filterVisibleNavRoutes(
    NAV_ROUTE_MANIFEST.filter(
      route =>
        route.section === section &&
        route.placements.includes(placement) &&
        !(
          options.excludeMobileBoardsDedicatedTab &&
          route.mobileExcludeFromBoardsSheet === true
        )
    ),
    context
  );

  const roots = visible
    .filter(route => route.parentId === undefined)
    .slice()
    .sort((a, b) => a.order - b.order);

  return roots.map(root => {
    const children = visible
      .filter(route => route.parentId === root.id)
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(
        (route): PresenterNavItem => ({
          id: route.id,
          name: route.label,
          link: route.path,
          hasStatus: route.statusKey !== undefined,
          statusKey: route.statusKey,
        })
      );

    const item: PresenterNavItem = {
      id: root.id,
      name: root.label,
      link: root.path,
      hasStatus: root.statusKey !== undefined,
      statusKey: root.statusKey,
    };
    if (children.length > 0) {
      item.children = children;
    }
    return item;
  });
}

export function getDesktopBoardItems(context: NavVisibilityContext): PresenterNavItem[] {
  return buildSectionTree(NAV_SECTION.BOARDS, NAV_PLACEMENT.DESKTOP, context);
}

function getDesktopPrimaryItems(context: NavVisibilityContext): PresenterNavItem[] {
  return buildSectionTree(NAV_SECTION.PRIMARY, NAV_PLACEMENT.DESKTOP, context);
}

const PRIMARY_PIN_ROUTE_IDS = new Set<NAV_ROUTE_ID>([NAV_ROUTE_ID.ATTRIBUTION]);

const PRIMARY_PIN_ORDER: readonly NAV_ROUTE_ID[] = [NAV_ROUTE_ID.ATTRIBUTION];

const REPORTS_PRIORITY_ORDER: readonly NAV_ROUTE_ID[] = [
  NAV_ROUTE_ID.PNL_DASHBOARD,
  NAV_ROUTE_ID.INCOME_STATEMENT,
];
const REPORTS_PRIORITY_ROUTE_IDS = new Set<NAV_ROUTE_ID>(REPORTS_PRIORITY_ORDER);

const REPORTS_ZONE_PEER_CAP = 5;

function capReportsZonePeers(reports: PresenterNavItem[]): PresenterNavItem[] {
  if (reports.length <= REPORTS_ZONE_PEER_CAP) {
    return reports;
  }

  const visiblePeerSlots = REPORTS_ZONE_PEER_CAP - 1;
  const visiblePeers = reports.slice(0, visiblePeerSlots);
  const overflowPeers = reports.slice(visiblePeerSlots);

  const moreDisclosure: PresenterNavItem = {
    id: NAV_ROUTE_ID.REPORTS_MORE,
    name: REPORTS_MORE_ITEM_NAME,
    // Non-navigating disclosure — no link; NavItemEntry routes on children only.
    hasStatus: false,
    children: overflowPeers,
  };

  return [...visiblePeers, moreDisclosure];
}

function orderReportsPeers(items: PresenterNavItem[]): PresenterNavItem[] {
  const reportsPriority: PresenterNavItem[] = [];
  const reportsRest: PresenterNavItem[] = [];
  const reportsTrailing: PresenterNavItem[] = [];

  for (const item of items) {
    if (REPORTS_PRIORITY_ROUTE_IDS.has(item.id)) {
      reportsPriority.push(item);
      continue;
    }
    if (item.id === NAV_ROUTE_ID.REALTIME) {
      reportsTrailing.push(item);
      continue;
    }
    reportsRest.push(item);
  }

  reportsPriority.sort(
    (a, b) =>
      REPORTS_PRIORITY_ORDER.indexOf(a.id) - REPORTS_PRIORITY_ORDER.indexOf(b.id)
  );

  return [...reportsPriority, ...reportsRest, ...reportsTrailing];
}

export function getDesktopDashboardZones(
  context: NavVisibilityContext
): DesktopDashboardZones {
  const boards = getDesktopBoardItems(context);
  const primaryPins = getDesktopPrimaryItems(context);
  const reportCandidates: PresenterNavItem[] = [];

  for (const item of boards) {
    if (PRIMARY_PIN_ROUTE_IDS.has(item.id)) {
      primaryPins.push(item);
      continue;
    }
    reportCandidates.push(item);
  }

  primaryPins.sort(
    (a, b) => PRIMARY_PIN_ORDER.indexOf(a.id) - PRIMARY_PIN_ORDER.indexOf(b.id)
  );

  return {
    primaryPins,
    reports: capReportsZonePeers(orderReportsPeers(reportCandidates)),
  };
}

export function getDesktopControlPanelItems(
  context: NavVisibilityContext
): PresenterNavItem[] {
  return buildSectionTree(NAV_SECTION.CONTROL_PANEL, NAV_PLACEMENT.DESKTOP, context);
}

export function getDesktopNonDashboardItems(
  context: NavVisibilityContext
): PresenterNavItem[] {
  return buildSectionTree(NAV_SECTION.NON_DASHBOARD, NAV_PLACEMENT.DESKTOP, context);
}

export function getMobileReportsItems(context: NavVisibilityContext): PresenterNavItem[] {
  const boards = buildSectionTree(NAV_SECTION.BOARDS, NAV_PLACEMENT.MOBILE, context, {
    excludeMobileBoardsDedicatedTab: true,
  });
  return capReportsZonePeers(orderReportsPeers(boards));
}

export function getMobileSystemItems(context: NavVisibilityContext): PresenterNavItem[] {
  return buildSectionTree(NAV_SECTION.CONTROL_PANEL, NAV_PLACEMENT.MOBILE, context);
}

export function getSettingsNavItems(
  context: NavVisibilityContext = { hasShopifyData: true, accountFeatureFlagNames: [] }
): PresenterNavItem[] {
  const sections = [
    NAV_SECTION.SETTINGS_WORKSPACE,
    NAV_SECTION.SETTINGS_TRACKING,
    NAV_SECTION.SETTINGS_DEVELOPER,
    NAV_SECTION.SETTINGS_PERSONAL,
  ];

  return sections.flatMap(section =>
    buildSectionTree(section, NAV_PLACEMENT.SETTINGS, context)
  );
}

export function toLegacyNavItems(items: PresenterNavItem[]): LegacyNavItem[] {
  return items.map(item => ({
    name: item.name,
    link: item.link,
    hasStatus: item.hasStatus || undefined,
    statusKey: item.statusKey,
    children: item.children ? toLegacyNavItems(item.children) : undefined,
  }));
}
